import {
  Button,
  Checkbox,
  Col,
  ColorPicker,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import _, { find } from 'lodash';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FaPencil, FaRepeat } from 'react-icons/fa6';
import { GoTrash } from 'react-icons/go';
import { HiOutlineTrash } from 'react-icons/hi';
import { IoAddOutline } from 'react-icons/io5';
import { useLocation, useParams } from 'react-router-dom';
import { PriorityOptions, ProgressOptions, RepeatOptions, TASk_TABS } from '../../constants';
import { formatAttachments, generateUniqueId } from '../../helper';
import { decodeId } from '../../helper/hashids';
import useLabels from '../../hooks/useLabels';
import useRestaurants from '../../hooks/useRestaurants';
import { sendTaskAssignedEmail } from '../../redux/actions/email';
import { addLabel, deleteLabel, updateLabel } from '../../redux/actions/label';
import { createTask, getTasks, updateTask } from '../../redux/actions/tasks';
import { getUploadUrl, uploadFileToS3 } from '../../redux/actions/upload';
import { getFileUrl } from '../../utils';
import CustomSchedule from '../CustomSchedule';
import UploadAttachment from '../UploadAttachment';
import UserAvatar from '../UserAvatar';

const { TextArea } = Input;
const { Option } = Select;

const CreateTask = ({ title, open, onClose, taskData, onSubmit }) => {
  const { id: hashedId } = useParams();
  const id = decodeId(hashedId);
  const { users, restaurants } = useRestaurants();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [form] = Form.useForm();
  const { labels } = useLabels();
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [checklist, setChecklist] = useState([
    { checklist_id: generateUniqueId(), title: '', status: 0 },
  ]);
  const [customSchedule, setCustomSchedule] = useState();
  const [selectLabel, setSelectedLabel] = useState([]);
  const [colorHex, setColorHex] = useState('#1677ff');
  const [formatHex, setFormatHex] = useState('hex');
  const [newLabel, setNewLabel] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [isCPOpen, setIsCPOpen] = useState(false);
  const [isLabelEdit, setIsLabelEdit] = useState(false);
  const [isChecklistEdit, setIsChecklistEdit] = useState(false);
  const [links, setLinks] = useState([]);
  const inputRefs = useRef({});

  const activeTab = queryParams.get('tab');
  const restaurant = restaurants.find((rest) => Number(rest.id) === Number(id));
  const { fileNames, list } = formatAttachments(fileList);

  const validAssigneeIds = _.intersection(
    taskData?.assignee_ids,
    users.map((user) => user.id)
  );
  const inValidAssigneeIds = _.difference(
    taskData?.assignee_ids,
    users.map((user) => user.id)
  );

  const handleFormSubmit = async (values) => {
    const { due_date, schedule } = values;
    const filterCheckList = checklist.filter((el) => el.title !== '');

    let scheduleName;

    switch (schedule.toLowerCase()) {
      case 'daily':
        scheduleName = 'day';
        break;
      case 'weekdays':
        scheduleName = 'weekdays';
        break;
      case 'weekly':
        scheduleName = 'week';
        break;
      case 'monthly':
        scheduleName = 'month';
        break;
      case 'yearly':
        scheduleName = 'year';
        break;
      default:
    }
    const formattedDueDate = due_date ? dayjs(due_date).format('YYYY-MM-DD HH:mm:ss') : null;

    const formattedData = {
      ...values,
      checklist: filterCheckList,
      schedule:
        schedule === 'custom'
          ? customSchedule
          : {
            frequency: schedule,
            type: schedule,
            summary: `Occurs every ${scheduleName}.`,
          },
    };

    const body = {
      restaurant_id: id,
      title: formattedData.title,
      due_date: formattedDueDate,
      notes: formattedData.notes,
      progress: formattedData.progress,
      priority: formattedData.priority,
      is_scheduled: formattedData?.schedule?.frequency !== 'does-not-repeat' ? 1 : 0,
      schedule_json:
        formattedData?.schedule?.frequency !== 'does-not-repeat' ? formattedData.schedule : null,
      labels: formattedData.labels ?? [],
      assignees: [...(formattedData?.assignees ?? []), ...(inValidAssigneeIds ?? [])],
      files: fileNames.length ? JSON.stringify(fileNames) : null,
      checklist: filterCheckList,
      links: links?.length ? JSON.stringify(links) : null
    };

    const res = taskData
      ? await updateTask(taskData.id, body, { toast: true })
      : await createTask(body);
    const isFilesMatch = _.isEqual(fileNames, taskData && JSON.parse(taskData?.files));

    if (res && !isFilesMatch) {
      const filteredList = _.filter(list, _.isObjectLike);
      const uploadPromises = [];
      filteredList.forEach((f) => {
        uploadPromises.push(
          getUploadUrl(f, 'tasks', res?.record_id ?? taskData.id)
            .then((uploadUrl) => uploadFileToS3(uploadUrl, f.file, f.file.type))
            .catch((error) => console.error('Error uploading file:', error))
        );
      });
    }

    if (res?.ack) {
      const filterAssignee = taskData?.assignees?.map((a) => a.id);
      const assignees = _.difference(values?.assignees, filterAssignee);
      if (assignees?.length)
        sendTaskAssignedEmail({
          task: {
            ...body,
            due_date: body?.due_date ? dayjs(body?.due_date).format('DD/MM/YYYY HH:mm') : '-',
            restaurant: restaurant?.title,
            priority: find(PriorityOptions, { value: body?.priority })?.label,
          },
          users: assignees,
        });
      getTasks({ id, filter: activeTab ?? TASk_TABS.DASHBOARD });
      form.resetFields();
      onClose();
    }
  };

  const userRender = (props) => {
    const { label, value, closable, onClose } = props;
    const user = users.find((user) => user.id === value);
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px',
          gap: '8px',
        }}
        color="blue"
      >
        <UserAvatar size="small" user={user} />
        <Typography.Text strong>{label}</Typography.Text>
      </Tag>
    );
  };

  const labelRender = (props) => {
    const { value, closable, onClose } = props;
    const tag = labels.find((tag) => {
      return String(tag.id) === String(value);
    });
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          marginInlineEnd: 4,
        }}
        color={tag?.color}
      >
        {tag?.title}
      </Tag>
    );
  };

  const handleAddLabel = async () => {
    if (newLabel) {
      const body = {
        restaurant_id: id,
        title: newLabel?.title,
        color: hexString,
      };
      const res = await addLabel(body);
      if (res?.ack) {
        const newLabelId = res.record_id;
        const updatedSelectedLabels = [...(form.getFieldValue('labels') || []), String(newLabelId)];
        form.setFieldsValue({ labels: updatedSelectedLabels });
        setSelectedLabel(updatedSelectedLabels);

        // Reset input
        setNewLabel({});
        setColorHex('#1677ff');
      }
    }
  };

  const handleLabelEdit = (label) => {
    setIsLabelEdit(true);
    setNewLabel({ title: label.title, id: label.id });
    setColorHex(label.color);
  };

  const handleUpdateLabel = async () => {
    const body = {
      id: newLabel?.id,
      restaurant_id: id,
      title: newLabel?.title,
      color: hexString,
    };
    const res = await updateLabel(body);
    if (res) {
      setIsLabelEdit(false);
      setNewLabel({});
      setColorHex('#1677ff');
    }
  };

  const handleInputChange = (id, value) => {
    setChecklist((prevChecklist) =>
      prevChecklist.map((item) => (item.checklist_id === id ? { ...item, title: value } : item))
    );
  };

  const handleCheckboxChange = (id) => {
    setChecklist((prevChecklist) =>
      prevChecklist.map((item) =>
        item.checklist_id === id ? { ...item, status: item.status === 1 ? 0 : 1 } : item
      )
    );
  };

  const handleAddItem = (id) => {
    setIsChecklistEdit(true);
    const hasEmptyItem = checklist.some((item) => item.title.trim() === '');
    if (hasEmptyItem) return;

    const currentItem = checklist.find((item) => item.checklist_id === id);

    if (currentItem?.title.trim() !== '') {
      setChecklist((prevChecklist) => [
        ...prevChecklist,
        { checklist_id: generateUniqueId(), title: '', status: 0 },
      ]);
    }
  };

  const handleKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem(id);
    }
  };

  const handleRemoveItem = (id) => {
    setChecklist((prevChecklist) => prevChecklist.filter((item) => item.checklist_id !== id));
  };

  const hexString = useMemo(
    () => (typeof colorHex === 'string' ? colorHex : colorHex?.toHexString()),
    [colorHex]
  );

  const availableLabels = _.differenceBy(
    labels,
    labels.filter((item) => selectLabel.includes(String(item.id))),
    'id'
  );

  const handleLinkChange = (id, field, value) => {
    setLinks((prevLinks) =>
      prevLinks.map((link) => (link.id === id ? { ...link, [field]: value } : link))
    );
  };

  const handleAddLink = () => {
    setLinks((prevLinks) => [
      ...prevLinks,
      { id: generateUniqueId(), title: '', url: '' },
    ]);
  };

  const handleRemoveLink = (id) => {
    setLinks((prevLinks) => prevLinks.filter((link) => link.id !== id));
  };

  useEffect(() => {
    const lastItemId = checklist[checklist.length - 1]?.checklist_id;
    if (isChecklistEdit && lastItemId && inputRefs.current[lastItemId]) {
      inputRefs.current[lastItemId].focus();
    }
  }, [isChecklistEdit, checklist]);

  useEffect(() => {
    if (taskData) {
      form.setFieldsValue({
        title: taskData?.title,
        assignees: validAssigneeIds,
        labels: taskData?.labels?.map((label) => String(label?.label_id)),
        due_date: taskData?.due_date ? dayjs(taskData?.due_date) : undefined,
        progress: taskData?.progress || 0,
        priority: taskData?.priority || 2,
        schedule: taskData?.schedule_json?.type || 'does-not-repeat',
        notes: taskData?.notes,
      });
      if (taskData?.checklist) {
        setChecklist(() => [
          ...taskData.checklist,
          { checklist_id: generateUniqueId(), title: '', status: 0 },
        ]);
      }
      if (taskData.labels.length)
        setSelectedLabel(taskData.labels.map((label) => String(label.label_id)));
      if (taskData.files) {
        const files = JSON.parse(taskData.files);
        const formatFiles = files.map((file) => {
          return {
            uid: file,
            name: file,
            status: 'done',
            url: getFileUrl('tasks', taskData?.id, file),
            payload: true,
          };
        });
        setFileList(formatFiles);
      }
      if (taskData?.links) {
        const parsedLinks = JSON.parse(taskData.links)
        setLinks(parsedLinks)
      }
    }
  }, []);

  return (
    <>
      <Modal
        title={taskData ? 'Edit Task' : (title ?? 'Add Task')}
        okText={taskData ? 'Update Task' : 'Add Task'}
        open={open}
        onOk={() => form.submit()}
        onCancel={onClose}
        width={1000}
        centered
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: '#00000015',
          },
          content: {
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFormSubmit}
          initialValues={{
            progress: 0,
            priority: 2,
            schedule: 'does-not-repeat',
          }}
          size="large"
        >
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please enter the task title!' }]}
          >
            <Input placeholder="Title" />
          </Form.Item>

          <Form.Item label="Assignee" name="assignees">
            <Select
              mode="multiple"
              placeholder="Assignees"
              optionLabelProp="label"
              style={{ width: '100%' }}
              tagRender={userRender}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users?.map((user) => (
                <Option
                  key={user.id}
                  value={user.id}
                  label={`${user.first_name} ${user.last_name}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserAvatar user={user} />
                    <Flex vertical>
                      <Typography.Text>{`${user.first_name} ${user.last_name}`}</Typography.Text>
                      <Typography.Text type="secondary">{user.email}</Typography.Text>
                    </Flex>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Label" name="labels">
            <Select
              mode="multiple"
              placeholder="Label"
              optionLabelProp="label"
              open={isOpen}
              tagRender={labelRender}
              onDropdownVisibleChange={(open) => !isCPOpen && setIsOpen(open)}
              onChange={(value) => setSelectedLabel(value)}
              dropdownRender={(menu) => (
                <Fragment>
                  {menu}
                  <Divider
                    style={{
                      margin: '8px 0',
                    }}
                  />
                  <Space
                    style={{
                      padding: '0 8px 4px',
                    }}
                  >
                    <Input
                      placeholder="Title"
                      value={newLabel?.title}
                      onChange={(e) => setNewLabel((prev) => ({ ...prev, title: e.target.value }))}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <ColorPicker
                      format={formatHex}
                      value={colorHex}
                      onChange={setColorHex}
                      onFormatChange={setFormatHex}
                      onOpenChange={(open) => setIsCPOpen(open)}
                    />
                    <Button
                      variant="filled"
                      color="primary"
                      icon={isLabelEdit ? <FaPencil /> : <IoAddOutline />}
                      onClick={isLabelEdit ? handleUpdateLabel : handleAddLabel}
                      disabled={!newLabel?.title?.trim() || !colorHex}
                    >
                      {isLabelEdit ? 'Edit' : 'Add'} Label
                    </Button>
                  </Space>
                </Fragment>
              )}
              dropdownStyle={{
                maxWidth: '400px',
              }}
            >
              {availableLabels?.map((item) => (
                <Option key={item?.id}>
                  <Flex align="center" justify="space-between" gap="small">
                    <Tag color={item.color}>{item?.title}</Tag>
                    <Space>
                      <Button
                        color="primary"
                        size="small"
                        variant="filled"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLabelEdit(item);
                        }}
                        icon={<FaPencil />}
                      />
                      <Button
                        size="small"
                        variant="filled"
                        color="danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteLabel(item.id, id);
                        }}
                        icon={<HiOutlineTrash />}
                      />
                    </Space>
                  </Flex>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item label="Progress" name="progress">
                <Select
                  options={ProgressOptions}
                  optionRender={(option) => (
                    <Space>
                      <span role="img" aria-label={option.data.label}>
                        {option.data.icon}
                      </span>

                      {option.data.label}
                    </Space>
                  )}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item label="Priority" name="priority">
                <Select
                  options={PriorityOptions}
                  optionRender={(option) => (
                    <Space>
                      <span role="img" aria-label={option.data.label}>
                        {option.data.icon}
                      </span>

                      {option.data.label}
                    </Space>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item
                label="Due Date"
                name="due_date"
                rules={[{ required: true, message: 'Please select a due date!' }]}
              >
                <DatePicker
                  format={'DD/MM/YYYY HH:mm'}
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf('day');
                  }}
                  placeholder="Due Date"
                  showToday={false}
                  showTime={{
                    minuteStep: 5,
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12}>
              <Form.Item label="Schedule" name="schedule">
                <Select
                  options={RepeatOptions}
                  prefix={<FaRepeat />}
                  onChange={(value) => {
                    if (value === 'custom') {
                      setIsScheduleModalOpen(true);
                    }
                  }}
                  optionRender={(option) => (
                    <div
                      role="button"
                      onClick={() => {
                        if (option.data.value === 'custom') setIsScheduleModalOpen(true);
                      }}
                    >
                      <span aria-label={option.data.label}>{option.data.icon}</span>

                      {option.data.label}
                    </div>
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={4} placeholder="Notes..." />
          </Form.Item>

          <Form.Item label="Checklist">
            {checklist?.map((item) => (
              <div key={item.checklist_id}>
                <Input
                  value={item.title}
                  ref={(el) => (inputRefs.current[item.checklist_id] = el)}
                  onChange={(e) => handleInputChange(item.checklist_id, e.target.value)}
                  onBlur={() => handleAddItem(item.id)}
                  onKeyPress={(e) => handleKeyPress(e, item.id)}
                  placeholder="Add an item"
                  variant="borderless"
                  style={{ flex: 1, marginRight: '8px' }}
                  prefix={
                    <Checkbox
                      checked={item.status}
                      onChange={() => handleCheckboxChange(item.checklist_id)}
                      style={{ marginRight: '8px' }}
                    />
                  }
                  suffix={
                    !!item.title && (
                      <GoTrash
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleRemoveItem(item.checklist_id)}
                      />
                    )
                  }
                />
              </div>
            ))}
          </Form.Item>

          <Form.Item label="Attachments" name="files">
            <UploadAttachment fileList={fileList} onChange={(file) => setFileList(file)} />
          </Form.Item>

          <Form.Item label="Links">
            <Space direction="vertical" style={{ width: '100%' }}>
              {links.map((link, index) => (
                <Row key={link.id} gutter={[16, 16]} align="middle" style={{ alignItems: 'baseline' }}>
                  <Col xs={24} md={6}>
                    <Form.Item
                      name={['links', index, 'title']}
                      rules={[{ required: true, message: 'Please enter a title' }]}
                      initialValue={link.title}
                    >
                      <Input
                        placeholder="Title"
                        value={link.title}
                        onChange={(e) => handleLinkChange(link.id, 'title', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name={['links', index, 'url']}
                      rules={[
                        { required: true, message: 'Please enter a URL' },
                        { type: 'url', message: 'Please enter a valid URL' },
                      ]}
                      initialValue={link.url}
                    >
                      <Input
                        placeholder="URL"
                        value={link.url}
                        onChange={(e) => handleLinkChange(link.id, 'url', e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={2}>
                    <Button
                      danger
                      icon={<GoTrash />}
                      onClick={() => handleRemoveLink(link.id)}
                      style={{ width: '100%' }}
                    />
                  </Col>
                </Row>
              ))}
              {links.length <= 2 && (
                <Button type="dashed" onClick={handleAddLink} icon={<IoAddOutline />}>
                  Add Link
                </Button>
              )}
            </Space>
          </Form.Item>

        </Form>
      </Modal>
      {isScheduleModalOpen && (
        <CustomSchedule
          open={isScheduleModalOpen}
          onClose={() => setIsScheduleModalOpen(false)}
          onSubmit={(val) => setCustomSchedule(val)}
          initialData={taskData?.schedule_json}
        />
      )}
    </>
  );
};

export default CreateTask;
